/**
 * TenantDetails Component
 *
 * Admin panel page for viewing detailed tenant deployment information.
 * Shows deployment timeline, certificate details, stack events, and CloudFront info.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../services/api';

interface ValidationRecord {
  name: string;
  type: string;
  value: string;
}

interface TimelineStep {
  step: string;
  label: string;
  status: string;
  timestamp?: string;
  details?: string;
}

interface TenantDetails {
  tenantId: string;
  photographer: {
    email: string | null;
    businessName: string | null;
    name: string | null;
  };
  deployment: {
    customDomain: string | null;
    status: string;
    startedAt: string | null;
    completedAt: string | null;
  };
  certificate: {
    arn: string | null;
    status: string | null;
    validationMethod: string;
    validationRecords: ValidationRecord[];
    issuedAt: string | null;
  };
  cloudFormation: {
    stackName: string;
    stackStatus: string | null;
    stackId: string | null;
    stackEvents: unknown[];
    stackOutputs: Record<string, string>;
    stackResources: unknown[];
    errorMessage: string | null;
  };
  cloudFront: {
    distributionId: string | null;
    distributionDomain: string | null;
    distributionStatus: string | null;
    customDomainCname: string | null;
  };
  timeline: TimelineStep[];
  actions: string[];
}

const STATUS_COLORS: Record<string, string> = {
  ACTIVE: 'bg-green-100 text-green-800',
  DEPLOYING: 'bg-blue-100 text-blue-800',
  PENDING_VALIDATION: 'bg-yellow-100 text-yellow-800',
  FAILED: 'bg-red-100 text-red-800',
  NOT_STARTED: 'bg-gray-100 text-gray-800',
  ISSUED: 'bg-green-100 text-green-800',
  PENDING: 'bg-yellow-100 text-yellow-800',
  CREATE_COMPLETE: 'bg-green-100 text-green-800',
  CREATE_IN_PROGRESS: 'bg-blue-100 text-blue-800',
  CREATE_FAILED: 'bg-red-100 text-red-800',
};

const STEP_ICONS: Record<string, string> = {
  complete: '✓',
  in_progress: '⏳',
  pending: '○',
  failed: '✗',
};

export const TenantDetails: React.FC = () => {
  const { tenantId } = useParams<{ tenantId: string }>();
  const navigate = useNavigate();
  const [details, setDetails] = useState<TenantDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const loadDetails = useCallback(async () => {
    if (!tenantId) return;

    setLoading(true);
    setError(null);

    try {
      const response = await api.get<{ data: TenantDetails }>(
        `/admin/tenants/${tenantId}`
      );
      setDetails(response.data.data);
    } catch {
      setError('Failed to load tenant details. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    loadDetails();
  }, [loadDetails]);

  // Auto-refresh for deploying status
  useEffect(() => {
    if (
      details?.deployment.status === 'DEPLOYING' ||
      details?.deployment.status === 'PENDING_VALIDATION'
    ) {
      const interval = setInterval(loadDetails, 10000);
      return () => clearInterval(interval);
    }
  }, [details?.deployment.status, loadDetails]);

  const handleRetry = async () => {
    setActionLoading('retry');
    try {
      await api.post(`/admin/tenants/${tenantId}/retry`);
      loadDetails();
    } catch {
      alert('Failed to retry deployment');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async () => {
    if (
      !confirm(
        'Are you sure you want to delete this tenant deployment? This will delete all AWS resources.'
      )
    ) {
      return;
    }

    setActionLoading('delete');
    try {
      await api.delete(`/admin/tenants/${tenantId}`);
      navigate('/admin/tenants');
    } catch {
      alert('Failed to delete tenant');
    } finally {
      setActionLoading(null);
    }
  };

  const handleTestDomain = async () => {
    setActionLoading('test');
    try {
      const response = await api.post<{ data: { checks: unknown[] } }>(
        `/admin/tenants/${tenantId}/test-domain`
      );
      alert(`Domain test results:\n${JSON.stringify(response.data.data.checks, null, 2)}`);
    } catch {
      alert('Failed to test domain');
    } finally {
      setActionLoading(null);
    }
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-500">Loading tenant details...</p>
        </div>
      </div>
    );
  }

  if (error || !details) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-4xl mx-auto">
          <button
            onClick={() => navigate('/admin/tenants')}
            className="mb-4 text-blue-600 hover:text-blue-800"
          >
            &larr; Back to Tenants
          </button>
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error || 'Tenant not found'}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/admin/tenants')}
            className="mb-4 text-blue-600 hover:text-blue-800"
          >
            &larr; Back to Tenants
          </button>

          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                {details.photographer.businessName || details.tenantId}
              </h1>
              <p className="text-gray-500 mt-1">
                {details.deployment.customDomain || 'No custom domain configured'}
              </p>
            </div>

            <span
              className={`px-3 py-1 text-sm font-semibold rounded-full ${
                STATUS_COLORS[details.deployment.status] || STATUS_COLORS.NOT_STARTED
              }`}
            >
              {details.deployment.status.replace(/_/g, ' ')}
            </span>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="flex gap-3 flex-wrap">
            <button
              onClick={loadDetails}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Refresh
            </button>

            {details.actions.includes('test_domain') && (
              <button
                onClick={handleTestDomain}
                disabled={actionLoading === 'test'}
                className="px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors disabled:opacity-50"
              >
                {actionLoading === 'test' ? 'Testing...' : 'Test Domain'}
              </button>
            )}

            {details.actions.includes('retry') && (
              <button
                onClick={handleRetry}
                disabled={actionLoading === 'retry'}
                className="px-4 py-2 bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200 transition-colors disabled:opacity-50"
              >
                {actionLoading === 'retry' ? 'Retrying...' : 'Retry Deployment'}
              </button>
            )}

            {details.actions.includes('delete') && (
              <button
                onClick={handleDelete}
                disabled={actionLoading === 'delete'}
                className="px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors disabled:opacity-50"
              >
                {actionLoading === 'delete' ? 'Deleting...' : 'Delete Tenant'}
              </button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Deployment Timeline */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold mb-4">Deployment Progress</h2>
            <div className="space-y-4">
              {details.timeline.length === 0 ? (
                <p className="text-gray-500">No deployment started</p>
              ) : (
                details.timeline.map((step, index) => (
                  <div key={index} className="flex items-start">
                    <span
                      className={`w-6 h-6 flex items-center justify-center rounded-full mr-3 ${
                        step.status === 'complete'
                          ? 'bg-green-100 text-green-600'
                          : step.status === 'in_progress'
                          ? 'bg-blue-100 text-blue-600'
                          : step.status === 'failed'
                          ? 'bg-red-100 text-red-600'
                          : 'bg-gray-100 text-gray-400'
                      }`}
                    >
                      {STEP_ICONS[step.status] || '○'}
                    </span>
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{step.label}</p>
                      {step.timestamp && (
                        <p className="text-sm text-gray-500">
                          {formatDate(step.timestamp)}
                        </p>
                      )}
                      {step.details && (
                        <p className="text-sm text-gray-400">{step.details}</p>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Certificate Details */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold mb-4">SSL Certificate</h2>
            <dl className="space-y-3">
              <div>
                <dt className="text-sm text-gray-500">Status</dt>
                <dd>
                  <span
                    className={`px-2 py-1 text-xs font-semibold rounded-full ${
                      STATUS_COLORS[details.certificate.status || ''] ||
                      STATUS_COLORS.PENDING
                    }`}
                  >
                    {details.certificate.status || 'Not Requested'}
                  </span>
                </dd>
              </div>
              <div>
                <dt className="text-sm text-gray-500">ARN</dt>
                <dd className="text-sm font-mono break-all">
                  {details.certificate.arn || '-'}
                </dd>
              </div>
              <div>
                <dt className="text-sm text-gray-500">Issued At</dt>
                <dd className="text-sm">{formatDate(details.certificate.issuedAt)}</dd>
              </div>

              {details.certificate.validationRecords.length > 0 && (
                <div>
                  <dt className="text-sm text-gray-500 mb-2">DNS Validation Records</dt>
                  <dd className="space-y-2">
                    {details.certificate.validationRecords.map((record, index) => (
                      <div
                        key={index}
                        className="bg-gray-50 p-2 rounded text-xs font-mono"
                      >
                        <div>
                          <span className="text-gray-500">Name:</span> {record.name}
                        </div>
                        <div>
                          <span className="text-gray-500">Type:</span> {record.type}
                        </div>
                        <div className="break-all">
                          <span className="text-gray-500">Value:</span> {record.value}
                        </div>
                      </div>
                    ))}
                  </dd>
                </div>
              )}
            </dl>
          </div>

          {/* CloudFormation Stack */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold mb-4">CloudFormation Stack</h2>
            <dl className="space-y-3">
              <div>
                <dt className="text-sm text-gray-500">Stack Name</dt>
                <dd className="text-sm font-medium">
                  {details.cloudFormation.stackName}
                </dd>
              </div>
              <div>
                <dt className="text-sm text-gray-500">Status</dt>
                <dd>
                  <span
                    className={`px-2 py-1 text-xs font-semibold rounded-full ${
                      STATUS_COLORS[details.cloudFormation.stackStatus || ''] ||
                      STATUS_COLORS.PENDING
                    }`}
                  >
                    {details.cloudFormation.stackStatus || 'Not Created'}
                  </span>
                </dd>
              </div>
              {details.cloudFormation.errorMessage && (
                <div>
                  <dt className="text-sm text-gray-500">Error</dt>
                  <dd className="text-sm text-red-600 bg-red-50 p-2 rounded">
                    {details.cloudFormation.errorMessage}
                  </dd>
                </div>
              )}
            </dl>
          </div>

          {/* CloudFront Distribution */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold mb-4">CloudFront Distribution</h2>
            <dl className="space-y-3">
              <div>
                <dt className="text-sm text-gray-500">Distribution ID</dt>
                <dd className="text-sm font-mono">
                  {details.cloudFront.distributionId || '-'}
                </dd>
              </div>
              <div>
                <dt className="text-sm text-gray-500">CloudFront Domain</dt>
                <dd className="text-sm font-mono">
                  {details.cloudFront.distributionDomain || '-'}
                </dd>
              </div>
              <div>
                <dt className="text-sm text-gray-500">Custom Domain CNAME</dt>
                <dd className="text-sm">
                  {details.cloudFront.customDomainCname || '-'}
                </dd>
              </div>
              <div>
                <dt className="text-sm text-gray-500">Status</dt>
                <dd>
                  <span
                    className={`px-2 py-1 text-xs font-semibold rounded-full ${
                      details.cloudFront.distributionStatus === 'Deployed'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-yellow-100 text-yellow-800'
                    }`}
                  >
                    {details.cloudFront.distributionStatus || 'Not Created'}
                  </span>
                </dd>
              </div>
            </dl>
          </div>
        </div>

        {/* Photographer Info */}
        <div className="bg-white rounded-lg shadow p-6 mt-6">
          <h2 className="text-lg font-semibold mb-4">Photographer Information</h2>
          <dl className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <dt className="text-sm text-gray-500">Tenant ID</dt>
              <dd className="text-sm font-mono">{details.tenantId}</dd>
            </div>
            <div>
              <dt className="text-sm text-gray-500">Business Name</dt>
              <dd className="text-sm">{details.photographer.businessName || '-'}</dd>
            </div>
            <div>
              <dt className="text-sm text-gray-500">Email</dt>
              <dd className="text-sm">{details.photographer.email || '-'}</dd>
            </div>
            <div>
              <dt className="text-sm text-gray-500">Started At</dt>
              <dd className="text-sm">{formatDate(details.deployment.startedAt)}</dd>
            </div>
            <div>
              <dt className="text-sm text-gray-500">Completed At</dt>
              <dd className="text-sm">{formatDate(details.deployment.completedAt)}</dd>
            </div>
          </dl>
        </div>
      </div>
    </div>
  );
};

export default TenantDetails;
